import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "./Icon";
import type { IconProps } from "./Icon";
import type { IconName } from "@/types/icons";
const arrowVariants = cva("overflow-visible", {
  variants: {
    color: {
      yellow: "text-yellow",
      purple: "text-purple",
      "purple-medium": "text-purple-medium",
      "purple-dark": "text-purple-dark",
      black: "text-black",
      white: "text-white"
    },
    variant: {
      small: "",
      large: ""
    },
    direction: {
      down: "",
      right: ""
    }
  },
  compoundVariants: [
  // Small variants
  {
    variant: "small",
    direction: "down",
    className: "w-5 h-[60px]"
  }, {
    variant: "small",
    direction: "right",
    className: "w-[60px] h-5"
  },
  // Large variants
  {
    variant: "large",
    direction: "down",
    className: "w-20 h-60"
  }, {
    variant: "large",
    direction: "right",
    className: "w-60 h-20"
  }],
  defaultVariants: {
    color: "purple",
    variant: "small",
    direction: "down"
  }
});
export interface ArrowProps extends Omit<IconProps, "name" | "color" | "direction">, VariantProps<typeof arrowVariants> {}

/**
 * MUUUH Arrow component with configurable size, direction, and color
 *
 * Uses separate SVGs for each direction to ensure proper sizing with fixed pixel dimensions.
 *
 * Usage:
 * ```tsx
 * <Arrow variant="small" direction="down" color="purple" />
 * <Arrow variant="large" direction="right" color="yellow" />
 * ```
 */
export const Arrow: React.FC<ArrowProps> = t0 => {
  const $ = _c(15);
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      variant: t1,
      direction: t2,
      color: t3,
      className: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    props = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  const variant = t1 === undefined ? "small" : t1;
  const direction = t2 === undefined ? "down" : t2;
  const color = t3 === undefined ? "purple" : t3;
  const className = t4 === undefined ? "" : t4;
  const iconName = `muuuh-arrow-${variant}-${direction}` as IconName;
  let t5;
  if ($[6] !== className || $[7] !== color || $[8] !== direction || $[9] !== variant) {
    t5 = arrowVariants({
      color,
      variant,
      direction,
      className
    });
    $[6] = className;
    $[7] = color;
    $[8] = direction;
    $[9] = variant;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== iconName || $[12] !== props || $[13] !== t5) {
    t6 = <Icon name={iconName} className={t5} {...props} />;
    $[11] = iconName;
    $[12] = props;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};